import React from 'react';
import { AutocompleteRenderInputParams, TextField, Stack, TextFieldVariants } from '@mui/material';
import LoadableAutocomplete, { LoadableAutocompleteProps } from './LoadableAutocomplete';

export type MultipleValueSelectorProps<T> = {
  label?: string;
  placeholder?: string;
  variant?: TextFieldVariants;
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode;
} & Omit<LoadableAutocompleteProps<T, boolean, boolean, boolean>, 'renderInput'> &
  // make the renderInput prop optional, it's provided by this component, if not specified
  Partial<Pick<LoadableAutocompleteProps<T, boolean, boolean, boolean>, 'renderInput'>>;

export function MultipleValueSelector<T>({
  getOptionLabel,
  renderInput,
  label,
  placeholder,
  loadingState,
  variant = 'standard',
  ...props
}: MultipleValueSelectorProps<T>) {
  const getLabel =
    getOptionLabel ??
    ((option: T | string): string => {
      if (typeof option === 'string') {
        return option;
      }

      if (option && typeof option === 'object' && 'name' in option) {
        return option.name as string;
      }

      return '';
    });

  const renderInputComponent =
    renderInput ??
    ((params: AutocompleteRenderInputParams) => (
      <TextField {...params} variant={variant} label={label} placeholder={placeholder} />
    ));

  return (
    <Stack direction='row' spacing={2} flexDirection={'row'} width={'100%'}>
      <LoadableAutocomplete<T, boolean, boolean, boolean>
        {...props}
        loadingState={loadingState || { status: 'Complete' }}
        multiple
        getOptionLabel={getLabel}
        renderInput={renderInputComponent}
        sx={{
          width: '100%',
          '& .MuiAutocomplete-tag': {
            whiteSpace: 'normal',
          },
          ...(props.sx ?? {}),
        }}
      />
    </Stack>
  );
}
